import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit } from '@angular/core';
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { ItemSearchSelectFilterModule, SelectedDropdownItem } from '@galaxy/inventory-ui';
import { App, EditionInterface } from '@galaxy/marketplace-apps';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Comparison, RuleInterface } from '@vendasta/automata';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { GalaxyWrapModule } from '@vendasta/galaxy/galaxy-wrap';
import { SelectInputOptionInterface } from '@vendasta/galaxy/input';
import { GalaxyPipesModule } from '@vendasta/galaxy/pipes';
import { ItemType, ListActivatableItemsFilters } from '@vendasta/marketplace-apps';
import { AddonInterface } from '@vendasta/marketplace-packages';
import { BehaviorSubject, combineLatest, Observable, of, Subject } from 'rxjs';
import { map, shareReplay, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { AutomationViewService } from '../../../../../../automation/automation-view.service';
import { ANY_ADDON, ANY_EDITION, ANY_TRIAL_OPTION, AppsService } from '../../../../../../data-services/apps.service';
import { CommonTriggerPanelComponent } from '../../../../common/common-trigger-panel.component';
import { ANY, EMPTY_PLACEHOLDER, NO_SELECTION } from '../constants';

@Component({
  selector: 'automata-product-activation-panel',
  templateUrl: './product-activation-panel.component.html',
  styleUrls: ['./product-activation-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    TranslateModule,
    GalaxyAlertModule,
    MatButtonModule,
    MatCardModule,
    FormsModule,
    GalaxyFormFieldModule,
    ReactiveFormsModule,
    MatInputModule,
    MatIconModule,
    MatSelectModule,
    GalaxyPipesModule,
    GalaxyWrapModule,
    ItemSearchSelectFilterModule,
  ],
})
export class ProductActivationPanelComponent extends CommonTriggerPanelComponent implements OnInit, OnDestroy {
  @Input() compareRejectedReason: boolean;
  @Input() descriptionTranslationId: string;
  @Input() includeAnyProducts = true;
  @Input() includeTrialSelection = true;
  apps$: Observable<App[]>;
  previousProduct: string;
  productControl: UntypedFormControl = new UntypedFormControl(NO_SELECTION, []);
  previousEditionOrAddon: string;
  editionsOrAddonsControl: UntypedFormControl = new UntypedFormControl(NO_SELECTION, []);
  addonControl: UntypedFormControl = new UntypedFormControl(NO_SELECTION, []);
  editionControl: UntypedFormControl = new UntypedFormControl(NO_SELECTION, []);
  trialControl: UntypedFormControl = new UntypedFormControl(NO_SELECTION, []);
  isLoading = true;
  appOptions$: Observable<SelectInputOptionInterface[]>;
  editionOptions$: Observable<SelectInputOptionInterface[]>;
  trialOptions$: Observable<SelectInputOptionInterface[]>;
  disableAddonSelection = false;
  addonOptions$: Observable<SelectInputOptionInterface[]>;
  editionsOrAddonsOptions: SelectInputOptionInterface[];

  disableProductAndEditionSelection$ = this.automationService.isNonPartnerAutomation().pipe(
    take(1),
    tap((isNonPartnerAutomation) => {
      if (isNonPartnerAutomation) {
        this.productControl.disable();
        this.editionControl.disable();
        this.editionsOrAddonsControl.disable();
      } else {
        this.productControl.setValidators(Validators.required);
      }
    }),
  );

  private selectedAppId$$ = new BehaviorSubject<string>('');
  selectedAppId$ = this.selectedAppId$$.asObservable();

  private selectedAddonId$$ = new BehaviorSubject<string>('');
  selectedAddonId$ = this.selectedAddonId$$.asObservable();

  destroyed$$ = new Subject<void>();

  ANY_EDITION = ANY_EDITION;
  ANY_ADDON = ANY_ADDON;
  ANY_PRODUCT = ANY;

  listActivatableItemsFilterForAppType = new ListActivatableItemsFilters({
    itemTypes: [ItemType.ITEM_TYPE_APP],
  });
  listActivatableItemsFilterForAddonType$: Observable<ListActivatableItemsFilters>;

  constructor(
    protected readonly automationService: AutomationViewService,
    protected readonly appsService: AppsService,
    protected readonly translateService: TranslateService,
  ) {
    super();
  }

  ngOnInit(): void {
    const comparisonRules = this.triggerChoices?.rules[0]?.value
      ? JSON.parse(this.triggerChoices?.rules[0]?.value)
      : [];

    const ruleDict = this.rulesToDict(comparisonRules);

    let initialEditionsOrAddonsSelected = ANY;
    if (
      ruleDict['addon']?.value !== undefined &&
      ruleDict['addon']?.value !== ANY &&
      ruleDict['addon']?.value !== ANY_EDITION
    ) {
      initialEditionsOrAddonsSelected = ANY_ADDON;
    } else if (ruleDict['edition']?.value !== undefined) {
      initialEditionsOrAddonsSelected = ANY_EDITION;
    }
    this.previousEditionOrAddon = initialEditionsOrAddonsSelected;
    let initialAddonSelected = NO_SELECTION;
    if (
      ruleDict['addon']?.value !== undefined &&
      ruleDict['addon']?.value !== ANY &&
      ruleDict['addon']?.value !== ANY_EDITION
    ) {
      initialAddonSelected = ruleDict['addon']?.value;
    }
    let initialEditionSelected = ruleDict['edition']?.value !== undefined ? ruleDict['edition']?.value : ANY_EDITION;
    if (initialEditionSelected === '') {
      initialEditionSelected = EMPTY_PLACEHOLDER;
    }
    const initialTrialOptionSelected =
      ruleDict['trial']?.value !== undefined ? ruleDict['trial']?.value.toString() : ANY_TRIAL_OPTION;
    const initialProductSelected$ = this.automationService.isBuiltInAutomationView().pipe(
      take(1),
      map(() => {
        // A saved trigger will always have at least one rule, but on first creation it will not
        let initialProductSelected = ruleDict['product'] ? ruleDict['product'].value : ANY;

        // These triggers were originally saved with empty value meaning Any Product
        // This is a lazy migration to still save Any in that way, but allow for an EMPTY selection as well.
        if (!initialProductSelected || initialProductSelected === EMPTY_PLACEHOLDER) {
          initialProductSelected = ANY;
        }

        this.selectedAppId$$.next(initialProductSelected);
        this.previousProduct = initialProductSelected;
        this.productControl.setValue(initialProductSelected);
        if (initialProductSelected !== ANY || initialAddonSelected !== NO_SELECTION) {
          this.setAddonOptions(initialAddonSelected);
        }
        return initialProductSelected;
      }),
    );

    this.apps$ = this.disableProductAndEditionSelection$.pipe(
      switchMap((disableProductAndEditionSelection) => {
        if (disableProductAndEditionSelection) {
          return of([]);
        }
        return this.appsService.getApps();
      }),
      tap(() => (this.isLoading = false)),
      tap(() => this.savingEnabled$.next(true)),
      take(1),
      shareReplay(1),
    );

    const appToOptions$ = this.apps$.pipe(
      map((apps) => {
        const any: SelectInputOptionInterface = {
          label: this.translateService.instant('AUTOMATIONS.EDITOR.TRIGGERS.PRODUCT_ACTIVATION.ANY_PRODUCT'),
          value: ANY,
        };
        return [any].concat(apps.map(this.mapAppToOption));
      }),
    );

    this.appOptions$ = combineLatest([appToOptions$, initialProductSelected$]).pipe(
      map(([options, initialProductSelected]) => {
        return options.map((option) => {
          if (option.value === initialProductSelected) {
            option.selected = true;
          }
          return option;
        });
      }),
      take(1),
    );

    this.productControl.valueChanges
      .pipe(
        takeUntil(this.destroyed$$),
        tap((value) => this.selectedAppId$$.next(value)),
      )
      .subscribe();

    this.addonControl.valueChanges
      .pipe(
        takeUntil(this.destroyed$$),
        tap((value) => this.selectedAddonId$$.next(value)),
      )
      .subscribe();

    this.editionsOrAddonsOptions = [
      {
        value: ANY,
        label: 'AUTOMATIONS.EDITOR.TRIGGERS.PRODUCT_ACTIVATION.EDITION_OR_ADDON',
      },
      {
        value: ANY_EDITION,
        label: 'AUTOMATIONS.EDITOR.TRIGGERS.PRODUCT_ACTIVATION.PRODUCT_EDITION',
      },
      {
        value: ANY_ADDON,
        label: 'AUTOMATIONS.EDITOR.TRIGGERS.PRODUCT_ACTIVATION.PRODUCT_ADDON',
      },
    ];

    this.editionOptions$ = combineLatest([this.selectedAppId$, this.apps$]).pipe(
      map(([appId, apps]) => {
        const app = apps.find((a) => a.key.appId === appId);
        const options = app?.editionInformation?.editions?.map(this.mapEditionToOption) || [];
        if (options.length > 0) {
          options.unshift({
            label: 'AUTOMATIONS.EDITOR.TRIGGERS.PRODUCT_ACTIVATION.ANY_EDITION',
            value: ANY_EDITION,
          });
          this.editionControl.enable();
        } else if (app) {
          options.push({
            label: app.sharedMarketingInformation.name,
            value: ANY_EDITION,
          });
          this.editionControl.disable();
        }
        return options;
      }),
      map((options) => {
        return this.setSelectedEditionOption(initialEditionSelected, options);
      }),
    );

    this.trialOptions$ = combineLatest([this.selectedAppId$, this.apps$]).pipe(
      map(([appId, apps]) => {
        const app = apps.find((a) => a.key.appId === appId);
        let options: SelectInputOptionInterface[] = [];
        if (app?.trialConfiguration?.trialEnabled) {
          options = [
            { label: 'AUTOMATIONS.EDITOR.TRIGGERS.PRODUCT_ACTIVATION.TRIAL', value: 'true' },
            { label: 'AUTOMATIONS.EDITOR.TRIGGERS.PRODUCT_ACTIVATION.NO_TRIAL', value: 'false' },
          ];
        } else {
          options = [];
        }
        if (options.length > 0) {
          options.unshift({
            label: 'AUTOMATIONS.EDITOR.TRIGGERS.PRODUCT_ACTIVATION.ANY_OPTION',
            value: ANY_TRIAL_OPTION,
          });
        }
        return options;
      }),
      map((options) => {
        return this.setSelectedTrialOptions(initialTrialOptionSelected, options);
      }),
    );

    this.editionsOrAddonsControl.setValue(initialEditionsOrAddonsSelected);
    this.editionControl.setValue(initialEditionSelected);
    this.trialControl.setValue(initialTrialOptionSelected);
    this.addonControl.setValue(initialAddonSelected);
    this.selectedAddonId$$.next(initialAddonSelected);

    this.productControl.valueChanges
      .pipe(
        tap((val) => {
          if (val !== this.previousProduct) {
            this.previousProduct = val;
            if (val !== ANY && val !== NO_SELECTION) {
              this.setAddonOptions(initialAddonSelected);
              this.editionsOrAddonsControl.setValue(ANY);
            }
          }
        }),
        takeUntil(this.destroyed$$),
      )
      .subscribe();
    this.editionsOrAddonsControl.valueChanges
      .pipe(
        tap((val) => {
          if (val !== this.previousEditionOrAddon) {
            this.previousEditionOrAddon = val;
            if (val === ANY_EDITION) {
              this.editionControl.setValue(ANY_EDITION);
              this.trialControl.setValue(ANY_TRIAL_OPTION);
              this.addonControl.setValue(NO_SELECTION);
            } else if (val === ANY_ADDON) {
              this.editionControl.setValue(ANY_EDITION);
              this.trialControl.setValue(NO_SELECTION);
              this.addonControl.setValue(ANY_ADDON);
            } else {
              this.editionControl.setValue(ANY_EDITION);
              this.trialControl.setValue(NO_SELECTION);
              this.addonControl.setValue(NO_SELECTION);
            }
          }
        }),
        takeUntil(this.destroyed$$),
      )
      .subscribe();

    this.listActivatableItemsFilterForAddonType$ = this.selectedAppId$.pipe(
      map((selectedAppId) => {
        return new ListActivatableItemsFilters({
          requiredItemId: selectedAppId,
          itemTypes: [ItemType.ITEM_TYPE_ADDON],
        });
      }),
    );
  }

  ngOnDestroy(): void {
    this.destroyed$$.next();
    this.destroyed$$.complete();
  }

  itemSelectionChanged(selectedDropdownItem: SelectedDropdownItem): void {
    const appId = selectedDropdownItem ? selectedDropdownItem.appId : '';
    this.productControl.setValue(appId);
    this.selectedAppId$$.next(appId);
  }

  addonSelectionChanged(selectedDropdownItem: SelectedDropdownItem): void {
    const addonId = selectedDropdownItem ? selectedDropdownItem.addonId : '';
    this.addonControl.setValue(addonId);
    this.selectedAddonId$$.next(addonId);
  }

  getControlsToValidateOnSave(): (UntypedFormControl | UntypedFormGroup | UntypedFormArray)[] {
    return [this.productControl];
  }

  isFormValid(): boolean {
    return !this.productControl.invalid;
  }

  setEditionOrAddonOptions(): void {
    if (this.disableAddonSelection) {
      this.editionsOrAddonsOptions[2].label = this.translateService.instant(
        'AUTOMATIONS.EDITOR.TRIGGERS.PRODUCT_ACTIVATION.NO_ADDONS',
      );
    } else {
      this.editionsOrAddonsOptions[2].label = this.translateService.instant(
        'AUTOMATIONS.EDITOR.TRIGGERS.PRODUCT_ACTIVATION.PRODUCT_ADDON',
      );
    }
  }

  setAddonOptions(initialAddonSelected): void {
    this.addonOptions$ = this.selectedAppId$.pipe(
      switchMap((appId) => this.appsService.getAddons(appId)),
      map((a) => {
        let options = [];
        if (a) {
          options = a.map(this.mapAddonToOption);
          if (options.length > 0) {
            options.unshift({
              label: 'AUTOMATIONS.EDITOR.TRIGGERS.PRODUCT_ACTIVATION.ANY_ADDON',
              value: ANY_ADDON,
            });
            this.disableAddonSelection = false;
          }
        }
        if (options.length === 0) {
          this.disableAddonSelection = true;
        }
        this.setEditionOrAddonOptions();
        return options;
      }),
      map((options) => {
        return this.setSelectedAddonOption(initialAddonSelected, options);
      }),
    );
  }

  getBaseRules(): RuleInterface[] {
    const initialComparisonRules = [];
    if (this.compareRejectedReason) {
      initialComparisonRules.push({
        comparison: Comparison.StringEquals,
        next: '',
        value: '',
        variable_path: '{.rejected_reason}',
      });
    }
    return [
      {
        comparison: Comparison.And,
        value: JSON.stringify(initialComparisonRules),
      },
    ];
  }

  rulesToDict(rules: any[]): { [key: string]: any } {
    const ruleDict = {
      rejected_reason: null,
      product: null,
      edition: null,
      trial: null,
      addon: null,
    };
    for (const rule of rules) {
      if (rule['variable_path'].includes('.is_trial')) {
        ruleDict['trial'] = rule;
      } else if (rule['variable_path'].includes('.app_id')) {
        ruleDict['product'] = rule;
      } else if (rule['variable_path'].includes('.edition_id')) {
        ruleDict['edition'] = rule;
      } else if (rule['variable_path'].includes('.addon_id')) {
        ruleDict['addon'] = rule;
      } else if (rule['variable_path'].includes('.rejected_reason')) {
        ruleDict['rejected_reason'] = rule;
      }
    }

    return ruleDict;
  }

  fillRuleValues(rules: RuleInterface[]): RuleInterface[] {
    const ruleDict = this.rulesToDict(JSON.parse(rules[0].value));
    if (this.productControl.value !== ANY && this.trialControl.value !== ANY_TRIAL_OPTION) {
      let v: boolean;
      if (this.trialControl.value === 'true') {
        v = true;
      } else if (this.trialControl.value === 'false') {
        v = false;
      }
      if (v !== undefined && v !== null) {
        ruleDict['trial'] = {
          comparison: Comparison.BoolEquals,
          next: '',
          value: v,
          variable_path: '{.is_trial}',
        };
      }
    } else {
      ruleDict['trial'] = null;
    }
    if (this.editionsOrAddonsControl.value === ANY_EDITION && this.editionControl.value === ANY_EDITION) {
      ruleDict['edition'] = {
        comparison: Comparison.AlwaysTrue,
        next: '',
        value: ANY_EDITION,
        variable_path: '{.edition_id}',
      };
    } else if (this.editionControl.value !== ANY_EDITION) {
      ruleDict['edition'] = {
        comparison: Comparison.StringEquals,
        next: '',
        value: this.editionControl.value === EMPTY_PLACEHOLDER ? NO_SELECTION : this.editionControl.value,
        variable_path: '{.edition_id}',
      };
    } else {
      ruleDict['edition'] = null;
    }
    if (this.editionsOrAddonsControl.value === ANY) {
      ruleDict['addon'] = {
        comparison: Comparison.AlwaysTrue,
        next: '',
        value: ANY,
        variable_path: '{.addon_id}',
      };
    } else if (this.addonControl.value === ANY_ADDON) {
      ruleDict['addon'] = {
        comparison: Comparison.StringExists,
        next: '',
        value: ANY_ADDON,
        variable_path: '{.addon_id}',
      };
    } else if (this.editionsOrAddonsControl.value === ANY_EDITION && this.editionControl.value === ANY_EDITION) {
      ruleDict['addon'] = {
        comparison: Comparison.Not,
        value: {
          comparison: Comparison.StringExists,
          next: '',
          value: ANY_EDITION,
          variable_path: '{.addon_id}',
        },
        variable_path: '',
      };
    } else if (this.addonControl.value !== NO_SELECTION && this.addonControl.value !== ANY_ADDON) {
      ruleDict['addon'] = {
        comparison: Comparison.StringEquals,
        next: '',
        value: this.addonControl.value,
        variable_path: '{.addon_id}',
      };
    } else {
      ruleDict['addon'] = null;
    }
    if (this.productControl.value !== ANY) {
      ruleDict['product'] = {
        comparison: Comparison.StringEquals,
        next: '',
        value: this.productControl.value || EMPTY_PLACEHOLDER,
        variable_path: '{.app_id}',
      };
    } else {
      ruleDict['product'] = null;
    }

    const comparisonRules = [];
    const order = ['rejected_reason', 'product', 'addon', 'edition', 'trial'];
    for (const key of order) {
      if (ruleDict[key]) {
        comparisonRules.push(ruleDict[key]);
      }
    }
    rules[0].value = JSON.stringify(comparisonRules);
    return rules;
  }

  mapAppToOption(app: App): SelectInputOptionInterface {
    return {
      label: app.sharedMarketingInformation?.name,
      value: app.key?.appId,
    };
  }

  mapEditionToOption(edition: EditionInterface): SelectInputOptionInterface {
    return {
      label: edition.name,
      value: edition.appKey?.editionId ? edition.appKey?.editionId : EMPTY_PLACEHOLDER,
    };
  }

  mapAddonToOption(addons: AddonInterface): SelectInputOptionInterface {
    return {
      label: addons.title,
      value: addons.addonId ? addons.addonId : EMPTY_PLACEHOLDER,
    };
  }

  setSelectedEditionOption(
    initialEditionSelected: string,
    options: SelectInputOptionInterface[],
  ): SelectInputOptionInterface[] {
    return options.map((option) => {
      if (
        option.value === initialEditionSelected ||
        (option.value === EMPTY_PLACEHOLDER && initialEditionSelected === NO_SELECTION)
      ) {
        option.selected = true;
      }
      return option;
    });
  }

  setSelectedTrialOptions(
    initialTrialOptionsSelected: string,
    options: SelectInputOptionInterface[],
  ): SelectInputOptionInterface[] {
    return options.map((option) => {
      if (option.value === initialTrialOptionsSelected) {
        option.selected = true;
      }
      if (option.value === ANY_TRIAL_OPTION && initialTrialOptionsSelected === NO_SELECTION) {
        option.selected = true;
      }
      return option;
    });
  }

  setSelectedAddonOption(
    initialAddonSelected: string,
    options: SelectInputOptionInterface[],
  ): SelectInputOptionInterface[] {
    return options.map((option) => {
      if (
        option.value === initialAddonSelected ||
        (option.value === EMPTY_PLACEHOLDER && initialAddonSelected === NO_SELECTION)
      ) {
        option.selected = true;
      }
      return option;
    });
  }
}
